/**
 * Tilemap collision outlines (TilemapCollider2D + CompositeCollider2D, geometry type Outlines).
 *
 * Each collidable tile is rasterized into a 16 px/unit mask (custom physics shape polygons, full
 * cell for grid colliders, otherwise sprite alpha), tile flips/rotations applied. The mask's pixel
 * boundary is traced into closed loops and simplified (Douglas-Peucker below one pixel, so pixel
 * staircases become straight slopes while 1 px steps survive).
 *
 * The raw (unsimplified) pixel loops are kept per tiles array so removing tiles at runtime
 * (BreakableTilemap) only re-rasterizes the cells around the change and re-traces the loops passing
 * through it.
 */
import type { PaletteEntry } from '../../model/format.ts';
import { matrixFromFlags } from '../../model/format.ts';
import type { GameData } from '../../data/gameData.ts';
import type { PixelSource } from './pixels.ts';

const RES = 16;
const SIMPLIFY_PX = 0.95;
/** margin (cells) for sprites larger than a cell */
const M = 2;

export type Loop = [number, number][];

interface TileShapeSource {
  palette: PaletteEntry[];
  gd: GameData;
  pixels: PixelSource;
}

/** one traced boundary loop: vertex ids (y * (W + 1) + x) in mask pixels, bounds and the simplified world loop */
interface RawLoop {
  v: Int32Array;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  loop: Loop | null;
}

/** mask frame (origin in cells, size in pixels) and the loops traced from it */
export interface TileCollisionState {
  ox: number;
  oy: number;
  W: number;
  H: number;
  raw: RawLoop[];
}

const states = new WeakMap<number[], TileCollisionState>();

/** Collider type of a palette entry: 0 none, 1 sprite shape, 2 full grid cell. */
export function paletteColliderType(gd: GameData, e: PaletteEntry | undefined): number {
  if (!e) return 0;
  if (e.tile) return gd.sprites.tiles[e.tile]?.collider ?? 1;
  return 1;
}

function pointInPolys(polys: [number, number][][], x: number, y: number): boolean {
  let inside = false;
  for (const poly of polys) {
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, yi] = poly[i], [xj, yj] = poly[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

function fillCell(mask: Uint8Array, W: number, H: number, baseX: number, baseY: number) {
  const x0 = Math.max(0, baseX), x1 = Math.min(W, baseX + RES);
  if (x1 <= x0) return;
  for (let y = Math.max(0, baseY); y < Math.min(H, baseY + RES); y++) mask.fill(1, y * W + x0, y * W + x1);
}

/** rasterize tile quad `i` into a W x H mask whose pixel (0, 0) is the corner of cell (ox, oy); clipped to the mask */
function rasterizeTile(tiles: number[], i: number, src: TileShapeSource, mask: Uint8Array, W: number, H: number, ox: number, oy: number) {
  const { palette, gd, pixels } = src;
  const tx = tiles[i], ty = tiles[i + 1], p = tiles[i + 2], f = tiles[i + 3];
  const e = palette[p];
  const ct = paletteColliderType(gd, e);
  if (!ct) return;
  const baseX = (tx - ox) * RES, baseY = (ty - oy) * RES;
  if (ct === 2) {
    fillCell(mask, W, H, baseX, baseY);
    return;
  }
  const s = e ? gd.spriteByName.get(`${e.tex}|${e.sprite}`) : undefined;
  if (!s) return;
  const [rx, ry, rw, rh] = s.def.r;
  const ppu = s.texDef.ppu;
  const [a, b, c, d] = matrixFromFlags(f);
  // sprite local bounds (units, pivot origin)
  const sx0 = -s.def.p[0] * (rw / ppu), sy0 = -s.def.p[1] * (rh / ppu);
  const sx1 = sx0 + rw / ppu, sy1 = sy0 + rh / ppu;
  // world (cell-centre relative) bounds after the tile matrix
  let qx0 = Infinity, qy0 = Infinity, qx1 = -Infinity, qy1 = -Infinity;
  for (const [px, py] of [[sx0, sy0], [sx1, sy0], [sx0, sy1], [sx1, sy1]]) {
    const qx = a * px + b * py, qy = c * px + d * py;
    qx0 = Math.min(qx0, qx); qy0 = Math.min(qy0, qy); qx1 = Math.max(qx1, qx); qy1 = Math.max(qy1, qy);
  }
  const shape = s.def.s && s.def.s.length ? s.def.s : null;
  const img = shape ? null : pixels.alpha(s.tex);
  if (!shape && !img) {
    // no pixel data: treat as full cell
    fillCell(mask, W, H, baseX, baseY);
    return;
  }
  const cx = tx + 0.5 - ox, cy = ty + 0.5 - oy; // cell centre in mask units
  const mx0 = Math.max(0, Math.floor((cx + qx0) * RES)), mx1 = Math.min(W, Math.ceil((cx + qx1) * RES));
  const my0 = Math.max(0, Math.floor((cy + qy0) * RES)), my1 = Math.min(H, Math.ceil((cy + qy1) * RES));
  for (let my = my0; my < my1; my++) {
    for (let mx = mx0; mx < mx1; mx++) {
      const qx = (mx + 0.5) / RES - cx, qy = (my + 0.5) / RES - cy;
      // inverse of an orthogonal flag matrix is its transpose
      const lx = a * qx + c * qy, ly = b * qx + d * qy;
      const u = (lx - sx0) * ppu, v = (ly - sy0) * ppu;
      if (u < 0 || v < 0 || u >= rw || v >= rh) continue;
      let on: boolean;
      if (shape) on = pointInPolys(shape, u - rw / 2, v - rh / 2);
      else {
        const px = rx + Math.floor(u), py = img!.h - 1 - (ry + Math.floor(v));
        on = px >= 0 && py >= 0 && px < img!.w && py < img!.h && img!.data[py * img!.w + px] > 0;
      }
      if (on) mask[my * W + mx] = 1;
    }
  }
}

type EdgeMap = Map<number, number[]>;

function addEdge(out: EdgeMap, from: number, to: number) {
  const l = out.get(from);
  if (l) l.push(to);
  else out.set(from, [to]);
}

/** boundary edges of pixel (x, y) (CCW around solid), vertex ids with row width VW */
function pixelEdges(out: EdgeMap, at: (x: number, y: number) => boolean, x: number, y: number, VW: number) {
  if (!at(x, y - 1)) addEdge(out, y * VW + x, y * VW + x + 1);
  if (!at(x + 1, y)) addEdge(out, y * VW + x + 1, (y + 1) * VW + x + 1);
  if (!at(x, y + 1)) addEdge(out, (y + 1) * VW + x + 1, (y + 1) * VW + x);
  if (!at(x - 1, y)) addEdge(out, (y + 1) * VW + x, y * VW + x);
}

/** follow the edge map into closed vertex loops (consumes the map) */
function traceRaw(out: EdgeMap, VW: number): number[][] {
  const loops: number[][] = [];
  const vx = (v: number) => v % VW, vy = (v: number) => Math.floor(v / VW);
  for (const [start, targets] of out) {
    while (targets.length) {
      const pts: number[] = [start];
      let prev = start;
      let cur = targets.pop()!;
      let guard = 0;
      while (cur !== start && guard++ < 10_000_000) {
        pts.push(cur);
        const nexts = out.get(cur);
        if (!nexts || !nexts.length) break;
        let pick = 0;
        if (nexts.length > 1) {
          // saddle: prefer the left turn relative to the incoming direction
          const dx = vx(cur) - vx(prev), dy = vy(cur) - vy(prev);
          let best = -Infinity;
          nexts.forEach((n, i) => {
            const ex = vx(n) - vx(cur), ey = vy(n) - vy(cur);
            const cross = dx * ey - dy * ex;
            if (cross > best) { best = cross; pick = i; }
          });
        }
        const nxt = nexts.splice(pick, 1)[0];
        prev = cur;
        cur = nxt;
      }
      if (pts.length >= 4) loops.push(pts);
    }
  }
  return loops;
}

function makeRaw(pts: number[], VW: number, ox: number, oy: number): RawLoop {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const xy: Loop = pts.map((v) => {
    const x = v % VW, y = Math.floor(v / VW);
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
    return [x, y];
  });
  const s = simplifyLoop(xy);
  return { v: Int32Array.from(pts), x0, y0, x1, y1, loop: s.length >= 3 ? s.map(([x, y]) => [x / RES + ox, y / RES + oy] as [number, number]) : null };
}

const loopsOf = (s: TileCollisionState): Loop[] => s.raw.filter((r) => r.loop).map((r) => r.loop!);

export function buildTileLoops(tiles: number[], src: TileShapeSource): Loop[] {
  const { palette, gd } = src;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const solid: number[] = [];
  for (let i = 0; i < tiles.length; i += 4) {
    const p = tiles[i + 2];
    if (p < 0) continue;
    if (!paletteColliderType(gd, palette[p])) continue;
    solid.push(i);
    x0 = Math.min(x0, tiles[i]); y0 = Math.min(y0, tiles[i + 1]);
    x1 = Math.max(x1, tiles[i] + 1); y1 = Math.max(y1, tiles[i + 1] + 1);
  }
  if (!solid.length) {
    states.delete(tiles);
    return [];
  }
  const ox = x0 - M, oy = y0 - M;
  const W = (x1 - x0 + 2 * M) * RES, H = (y1 - y0 + 2 * M) * RES;
  const mask = new Uint8Array(W * H);
  for (const i of solid) rasterizeTile(tiles, i, src, mask, W, H, ox, oy);

  const VW = W + 1;
  const out: EdgeMap = new Map();
  const at = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && mask[y * W + x] === 1;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (mask[y * W + x]) pixelEdges(out, at, x, y, VW);
  const state: TileCollisionState = { ox, oy, W, H, raw: traceRaw(out, VW).map((pts) => makeRaw(pts, VW, ox, oy)) };
  states.set(tiles, state);
  return loopsOf(state);
}

/**
 * Loops for `tiles` after removing `cells` from `prevTiles` (TilemapCollider2D.ProcessTilemapChanges).
 * Incremental when the previous tiles were traced by buildTileLoops / this function, else a full rebuild.
 */
export function tileLoopsAfterRemoval(prevTiles: number[], tiles: number[], cells: [number, number][], src: TileShapeSource): Loop[] {
  const prev = states.get(prevTiles);
  if (!prev) return buildTileLoops(tiles, src);
  const index = new Map<string, number>();
  for (let i = 0; i < tiles.length; i += 4) if (tiles[i + 2] >= 0) index.set(`${tiles[i]},${tiles[i + 1]}`, i);
  let st = prev;
  for (const [cx, cy] of cells) st = removeCell(st, tiles, index, cx, cy, src);
  states.set(tiles, st);
  return loopsOf(st);
}

function removeCell(st: TileCollisionState, tiles: number[], index: Map<string, number>, cx: number, cy: number, src: TileShapeSource): TileCollisionState {
  const { ox, oy, W, H } = st;
  const VW = W + 1;
  // R: pixels any sprite of the removed cell may have covered; E: pixels whose edges may change
  const rx0 = (cx - M - ox) * RES, rx1 = (cx + M + 1 - ox) * RES - 1;
  const ry0 = (cy - M - oy) * RES, ry1 = (cy + M + 1 - oy) * RES - 1;
  const ex0 = rx0 - 1, ex1 = rx1 + 1, ey0 = ry0 - 1, ey1 = ry1 + 1;

  // re-rasterize a window (one cell beyond R) from the remaining tiles that can reach it
  const wcx0 = cx - M - 1, wcy0 = cy - M - 1, wn = 2 * M + 3;
  const WW = wn * RES;
  const win = new Uint8Array(WW * WW);
  for (let y = cy - 2 * M - 1; y < cy + 2 * M + 2; y++) {
    for (let x = cx - 2 * M - 1; x < cx + 2 * M + 2; x++) {
      const i = index.get(`${x},${y}`);
      if (i !== undefined) rasterizeTile(tiles, i, src, win, WW, WW, wcx0, wcy0);
    }
  }
  const gx0 = (wcx0 - ox) * RES, gy0 = (wcy0 - oy) * RES;
  const at = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= W || y >= H) return false;
    const lx = x - gx0, ly = y - gy0;
    return lx >= 0 && ly >= 0 && lx < WW && ly < WW && win[ly * WW + lx] === 1;
  };

  // loops with a vertex on a pixel of E are re-traced; all others are unchanged
  const vx0 = ex0, vx1 = ex1 + 1, vy0 = ey0, vy1 = ey1 + 1;
  const keep: RawLoop[] = [];
  const affected: RawLoop[] = [];
  for (const r of st.raw) {
    let hit = false;
    if (r.x1 >= vx0 && r.x0 <= vx1 && r.y1 >= vy0 && r.y0 <= vy1) {
      for (const v of r.v) {
        const x = v % VW, y = (v - x) / VW;
        if (x >= vx0 && x <= vx1 && y >= vy0 && y <= vy1) {
          hit = true;
          break;
        }
      }
    }
    (hit ? affected : keep).push(r);
  }
  if (!affected.length && !hasSolid(at, ex0, ex1, ey0, ey1)) return st;

  const inE = (x: number, y: number) => x >= ex0 && x <= ex1 && y >= ey0 && y <= ey1;
  const out: EdgeMap = new Map();
  for (const r of affected) {
    const v = r.v, n = v.length;
    for (let k = 0; k < n; k++) {
      const a = v[k], b = v[(k + 1) % n];
      const ax = a % VW, ay = (a - ax) / VW, bx = b % VW, by = (b - bx) / VW;
      // pixel that generated the edge (see pixelEdges)
      let px: number, py: number;
      if (bx > ax) { px = ax; py = ay; }
      else if (by > ay) { px = ax - 1; py = ay; }
      else if (bx < ax) { px = bx; py = by - 1; }
      else { px = bx; py = by; }
      if (!inE(px, py)) addEdge(out, a, b);
    }
  }
  for (let y = Math.max(0, ey0); y <= Math.min(H - 1, ey1); y++) {
    for (let x = Math.max(0, ex0); x <= Math.min(W - 1, ex1); x++) if (at(x, y)) pixelEdges(out, at, x, y, VW);
  }
  const traced = traceRaw(out, VW).map((pts) => makeRaw(pts, VW, ox, oy));
  return { ox, oy, W, H, raw: [...keep, ...traced] };
}

function hasSolid(at: (x: number, y: number) => boolean, x0: number, x1: number, y0: number, y1: number) {
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if (at(x, y)) return true;
  return false;
}

/** Trace the pixel boundary of a mask into closed CCW (outer) / CW (hole) loops in pixel units. */
export function traceMask(mask: Uint8Array, W: number, H: number): Loop[] {
  const VW = W + 1;
  const out: EdgeMap = new Map();
  const at = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && mask[y * W + x] === 1;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (mask[y * W + x]) pixelEdges(out, at, x, y, VW);
  const loops: Loop[] = [];
  for (const pts of traceRaw(out, VW)) {
    const loop = simplifyLoop(pts.map((v) => [v % VW, Math.floor(v / VW)] as [number, number]));
    if (loop.length >= 3) loops.push(loop);
  }
  return loops;
}

function removeCollinear(pts: Loop): Loop {
  const n = pts.length;
  const res: Loop = [];
  for (let i = 0; i < n; i++) {
    const p = pts[(i + n - 1) % n], c = pts[i], q = pts[(i + 1) % n];
    const cross = (c[0] - p[0]) * (q[1] - c[1]) - (c[1] - p[1]) * (q[0] - c[0]);
    if (cross !== 0) res.push(c);
  }
  return res;
}

function dp(pts: Loop, i0: number, i1: number, eps: number, keep: Uint8Array) {
  const [ax, ay] = pts[i0], [bx, by] = pts[i1];
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  let best = -1, bi = -1;
  for (let i = i0 + 1; i < i1; i++) {
    const d = Math.abs((pts[i][0] - ax) * dy - (pts[i][1] - ay) * dx) / len;
    if (d > best) { best = d; bi = i; }
  }
  if (bi >= 0 && best > eps) {
    keep[bi] = 1;
    dp(pts, i0, bi, eps, keep);
    dp(pts, bi, i1, eps, keep);
  }
}

function simplifyLoop(raw: Loop): Loop {
  let pts = removeCollinear(raw);
  if (pts.length < 4) return pts;
  // rotate so the loop starts at an extreme (always kept) vertex
  let si = 0;
  for (let i = 1; i < pts.length; i++) if (pts[i][0] < pts[si][0] || (pts[i][0] === pts[si][0] && pts[i][1] < pts[si][1])) si = i;
  pts = [...pts.slice(si), ...pts.slice(0, si)];
  let fi = 0, fd = -1;
  for (let i = 1; i < pts.length; i++) {
    const d = (pts[i][0] - pts[0][0]) ** 2 + (pts[i][1] - pts[0][1]) ** 2;
    if (d > fd) { fd = d; fi = i; }
  }
  const closed = [...pts, pts[0]];
  const keep = new Uint8Array(closed.length);
  keep[0] = keep[fi] = keep[closed.length - 1] = 1;
  dp(closed, 0, fi, SIMPLIFY_PX, keep);
  dp(closed, fi, closed.length - 1, SIMPLIFY_PX, keep);
  const res: Loop = [];
  for (let i = 0; i < closed.length - 1; i++) if (keep[i]) res.push(closed[i]);
  return res;
}

/** Signed area (positive = CCW). */
export function loopArea(l: Loop): number {
  let a = 0;
  for (let i = 0, j = l.length - 1; i < l.length; j = i++) a += (l[j][0] * l[i][1] - l[i][0] * l[j][1]);
  return a / 2;
}
